import type { ChangeEvent } from 'react';
import { useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FiTrash2, FiDownload } from 'react-icons/fi';
import { filesService } from 'src/api/services/files';
import type { FileRecord } from 'src/types';
import './FileManager.css';

interface FileManagerProps {
  files: FileRecord[];
  isLoading: boolean;
  onUpload: (file: File) => void;
  onDelete: (fileId: string) => void;
  isUploading: boolean;
  accept?: string;
  label?: string;
}

const FileManager = ({
  files,
  isLoading,
  onUpload,
  onDelete,
  isUploading,
  accept,
  label,
}: FileManagerProps) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) onUpload(file);
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onUpload(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDownload = async (file: FileRecord) => {
    const blob = await filesService.download(file.id);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.original_filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="file-manager">
      {label && <h4 className="file-manager-label">{label}</h4>}

      <div
        className={`file-manager-dropzone ${isDragOver ? 'file-manager-dropzone--active' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        <span className="file-manager-dropzone-text">
          {isUploading ? t('management.saving') : t('management.files.dragDrop')}
        </span>
      </div>

      {isLoading ? (
        <p className="file-manager-status">{t('management.saving')}</p>
      ) : files.length === 0 ? (
        <p className="file-manager-status">{t('management.files.noFiles')}</p>
      ) : (
        <ul className="file-manager-list">
          {files.map((file) => (
            <li key={file.id} className="file-manager-item">
              <span className="file-manager-item-name" title={file.original_filename}>
                {file.original_filename}
              </span>
              <span className="file-manager-item-size">{formatSize(file.size_bytes)}</span>
              <div className="file-manager-item-actions">
                <button
                  className="data-table-action-btn"
                  onClick={() => void handleDownload(file)}
                  title="Download"
                >
                  <FiDownload />
                </button>
                <button
                  className="data-table-action-btn data-table-action-btn--danger"
                  onClick={() => onDelete(file.id)}
                  title={t('management.delete')}
                >
                  <FiTrash2 />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default FileManager;
